import React, { useState, useEffect } from 'react';
import { warehouseService } from '../../services/warehouseService';
import { useLocation } from '../../contexts/LocationContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  AlertBanner,
  TabSwitcher,
  DetailsModal,
} from './WarehouseDocumentBase';

const ExternalReceipt = () => {
  const { locationId } = useLocation();
  const { hasPermission } = usePermissions();
  const canGeneratePZ = hasPermission('faktury_zakupu', 'generuj_pz', locationId);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Historia PZ
  const [receipts, setReceipts] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal szczegolow PZ
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'new') {
      loadInvoices();
    } else if (activeTab === 'history' && locationId) {
      loadReceipts();
    }
  }, [activeTab]);

  useEffect(() => {
    if (locationId && activeTab === 'history') {
      loadReceipts();
    }
  }, [locationId]);

  const loadReceipts = async () => {
    try {
      const result = await warehouseService.getExternalReceipts({
        date: dateFilter,
        status: statusFilter,
        location_id: locationId
      });
      if (result.success) {
        setReceipts(result.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania historii PZ:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadReceipts();
    }
  }, [dateFilter, statusFilter]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const result = await warehouseService.getPurchaseInvoices();
      if (result.success) {
        setInvoices(result.data || []);
      } else {
        setError(result.error || 'Błąd podczas ładowania faktur');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const generatePZ = async (invoiceId) => {
    setLoading(true);
    try {
      const result = await warehouseService.generateExternalReceipt(invoiceId, {
        location_id: locationId,
        warehouse_id: locationId
      });
      if (result.success) {
        const docNum = result.data?.document_number || '';
        setSuccess(result.message || `PZ ${docNum} został wygenerowany pomyślnie`);
        setTimeout(() => setSuccess(''), 5000);
        loadInvoices();
        loadReceipts();
      } else {
        setError(result.error || 'Błąd podczas generowania PZ');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const viewReceiptDetails = async (receiptId) => {
    setDetailsLoading(true);
    try {
      const result = await warehouseService.getExternalReceiptDetails(receiptId);
      if (result.success) {
        setSelectedReceipt(result.data);
        setShowDetailsModal(true);
      } else {
        setError(result.error || 'Błąd pobierania szczegółów PZ');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setDetailsLoading(false);
    }
  };

  const downloadPDF = async (receiptId) => {
    setLoading(true);
    try {
      const result = await warehouseService.downloadExternalReceiptPDF(receiptId);
      if (result.success) {
        setSuccess('PDF został pobrany');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Błąd pobierania PDF');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid" style={{ fontSize: '12px' }}>
      <div className="d-flex justify-content-between align-items-center mb-2" style={{ padding: '0.3rem 0' }}>
        <h5 style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>📥 Przyj{'ę'}cie Zewn{'ę'}trzne (PZ)</h5>
      </div>

      <TabSwitcher
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          { key: 'new', label: 'Generowanie PZ', icon: 'fas fa-file-invoice', activeColor: 'var(--success)' },
          { key: 'history', label: 'Historia PZ', icon: 'fas fa-history', activeColor: 'var(--purple)' },
        ]}
      />

      <AlertBanner type="error" message={error} onClose={() => setError('')} />
      <AlertBanner type="success" message={success} onClose={() => setSuccess('')} />

      {/* New tab - invoice list */}
      {activeTab === 'new' && (
        <div className="row">
          <div className="col-12">
            <div className="card" style={{ fontSize: '11px' }}>
              <div className="card-header" style={{ padding: '0.4rem 0.75rem' }}>
                <h6 className="card-title mb-0" style={{ fontSize: '12px', fontWeight: '600' }}>Faktury zakupu dost{'ę'}pne do przyj{'ę'}cia</h6>
                <small style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
                  PZ generuje si{'ę'} automatycznie na podstawie faktury zakupu produkt{'ó'}w
                </small>
              </div>
              <div className="card-body" style={{ padding: '0.5rem 0.75rem' }}>
                {loading ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">{'Ł'}adowanie...</span>
                    </div>
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-3">
                    <div style={{ color: 'var(--text-muted)' }}>
                      <i className="fas fa-inbox mb-1" style={{ fontSize: '1.5rem' }}></i>
                      <p style={{ fontSize: '11px', margin: 0 }}>Brak faktur zakupu do przetworzenia</p>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Numer faktury', 'Dostawca', 'Data faktury', 'Wartość', 'Status PZ', 'Akcje'].map((h, i) => (
                            <th key={i} style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <strong>{invoice.invoice_number}</strong>
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              {invoice.supplier_name}
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              {new Date(invoice.invoice_date).toLocaleDateString('pl-PL')}
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              <strong>{parseFloat(invoice.total_amount || 0).toFixed(2)} z{'ł'}</strong>
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              {invoice.pz_generated ? (
                                <span style={{ fontSize: '10px', padding: '2px 6px', border: '1px solid var(--success)', color: 'var(--success)' , borderRadius: '3px' }}>
                                  <i className="fas fa-check me-1"></i>
                                  PZ wygenerowane
                                </span>
                              ) : (
                                <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'var(--warning)', color: 'var(--text)', borderRadius: '3px' }}>
                                  <i className="fas fa-clock me-1"></i>
                                  Oczekuje na PZ
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>
                              {!invoice.pz_generated ? (
                                canGeneratePZ ? (
                                <button
                                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' , cursor: 'pointer' }}
                                  onClick={() => generatePZ(invoice.id)}
                                  disabled={loading}
                                >
                                  <i className="fas fa-plus me-1"></i>
                                  Generuj PZ
                                </button>
                                ) : (
                                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Brak uprawnie{'ń'} PZ</span>
                                )
                              ) : (
                                <button
                                  style={{ padding: '4px 10px', fontSize: '11px', backgroundColor: 'var(--border)', color: 'var(--text-muted)' }}
                                  disabled
                                >
                                  <i className="fas fa-check me-1"></i>
                                  Zako{'ń'}czone
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History tab */}
      {activeTab === 'history' && (
        <div className="card" style={{ fontSize: '11px' }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ padding: '0.4rem 0.75rem' }}>
            <h6 className="card-title mb-0" style={{ fontSize: '12px', fontWeight: '600' }}>Historia Przyj{'ęć'} Zewn{'ę'}trznych</h6>
            <div className="d-flex gap-2">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={{ width: '130px', fontSize: '12px', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: '110px', fontSize: '12px', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              >
                <option value="all">Wszystkie</option>
                <option value="completed">Zako{'ń'}czone</option>
                <option value="pending">Oczekuj{'ą'}ce</option>
              </select>
              <button
                style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={loadReceipts}
              >
                <i className="fas fa-refresh"></i>
              </button>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0.5rem 0.75rem' }}>
            {receipts.length === 0 ? (
              <div className="text-center py-3">
                <div style={{ color: 'var(--text-muted)' }}>
                  <i className="fas fa-inbox mb-1" style={{ fontSize: '1.5rem' }}></i>
                  <p style={{ fontSize: '11px', margin: 0 }}>Brak przyj{'ęć'} zewn{'ę'}trznych</p>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Numer dokumentu', 'Faktura źródłowa', 'Dostawca', 'Data', 'Produkty', 'Wartość', 'Status', 'Akcje'].map((h, i) => (
                        <th key={i} style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map((receipt) => (
                      <tr key={receipt.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <strong>{receipt.document_number}</strong>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <small style={{ color: 'var(--text-muted)' }}>{receipt.source_invoice_number}</small>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          {receipt.supplier_name}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          {new Date(receipt.receipt_date).toLocaleDateString('pl-PL')}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'var(--cyan)', color: '#fff' , borderRadius: '3px' }}>
                            {receipt.items_count || 0} pozycji
                          </span>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <strong>{receipt.total_amount ? `${parseFloat(receipt.total_amount).toFixed(2)} zł` : '-'}</strong>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            color: '#fff' ,
                            borderRadius: '3px',
                            backgroundColor: receipt.status === 'completed' ? 'var(--success)' : 'var(--warning)'
                          }}>
                            {receipt.status === 'completed' ? 'Zakończone' : 'Oczekujące'}
                          </span>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <button
                            style={{ padding: '4px 10px', fontSize: '11px', backgroundColor: 'var(--card-bg)', color: 'var(--info)', border: '1px solid var(--info)', marginRight: '0.25rem', cursor: 'pointer' }}
                            onClick={() => viewReceiptDetails(receipt.id)}
                            disabled={detailsLoading}
                            title="Zobacz szczegóły"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            style={{ padding: '4px 10px', fontSize: '11px', backgroundColor: 'var(--card-bg)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => downloadPDF(receipt.id)}
                            disabled={loading}
                            title="Pobierz PDF"
                          >
                            <i className="fas fa-file-pdf"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Details modal */}
      <DetailsModal
        show={showDetailsModal && !!selectedReceipt}
        onClose={() => setShowDetailsModal(false)}
        title={`Szczegóły PZ: ${selectedReceipt?.document_number || ''}`}
        headerBg="linear-gradient(135deg, var(--info), var(--info))"
        maxWidth="700px"
        footerButtons={
          <>
            <button
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--info)', color: 'var(--info)' , cursor: 'pointer' }}
              onClick={() => downloadPDF(selectedReceipt.id)}
              disabled={loading}
            >
              <i className="fas fa-file-pdf me-1"></i>
              Pobierz PDF
            </button>
            <button
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , cursor: 'pointer' }}
              onClick={() => setShowDetailsModal(false)}
            >
              Zamknij
            </button>
          </>
        }
      >
        {selectedReceipt && (
          <div style={{ fontSize: '11px' }}>
            {/* Header info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px' }}>
              <div>
                <p style={{ margin: '0.25rem 0' }}><strong>Data przyj{'ę'}cia:</strong> {selectedReceipt.receipt_date ? new Date(selectedReceipt.receipt_date).toLocaleDateString('pl-PL') : '-'}</p>
                <p style={{ margin: '0.25rem 0' }}><strong>Dostawca:</strong> {selectedReceipt.supplier_name || '-'}</p>
              </div>
              <div>
                <p style={{ margin: '0.25rem 0' }}><strong>Faktura {'ź'}r{'ó'}d{'ł'}owa:</strong> {selectedReceipt.source_invoice_number || '-'}</p>
                <p style={{ margin: '0.25rem 0' }}><strong>Status:</strong>
                  <span style={{
                    marginLeft: '0.5rem',
                    fontSize: '10px',
                    padding: '2px 6px',
                    color: '#fff' ,
                    borderRadius: '3px',
                    backgroundColor: selectedReceipt.status === 'completed' ? 'var(--success)' : 'var(--warning)'
                  }}>
                    {selectedReceipt.status === 'completed' ? 'Zakończone' : 'Oczekujące'}
                  </span>
                </p>
              </div>
            </div>

            {/* Items table */}
            <h6 style={{ fontSize: '11px', fontWeight: '600', marginBottom: '0.5rem' }}>📦 Pozycje dokumentu:</h6>
            {selectedReceipt.items && selectedReceipt.items.length > 0 ? (
              <div className="table-responsive">
                <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: 'var(--text)', color: '#fff' }}>
                    <tr>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Lp.</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Nazwa produktu</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Kod kreskowy</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>Ilo{'ść'}</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>J.m.</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>Cena netto</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>Warto{'ść'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReceipt.items.map((item, index) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.3rem 0.4rem' }}>{index + 1}</td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>{item.product_name || '-'}</td>
                        <td style={{ padding: '0.3rem 0.4rem' }}><small style={{ color: 'var(--text-muted)' }}>{item.barcode || '-'}</small></td>
                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>{parseFloat(item.quantity || 0).toFixed(2)}</td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>{item.unit || 'szt'}</td>
                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>{parseFloat(item.unit_price || 0).toFixed(2)} z{'ł'}</td>
                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}><strong>{parseFloat(item.total_price || 0).toFixed(2)} z{'ł'}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot style={{ backgroundColor: 'var(--bg)' }}>
                    <tr>
                      <td colSpan="6" style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}><strong>RAZEM:</strong></td>
                      <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>
                        <strong>
                          {parseFloat(selectedReceipt.items.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0)).toFixed(2)} z{'ł'}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div style={{ padding: '0.5rem', backgroundColor: 'var(--info-light)', border: '1px solid var(--info-light)', borderRadius: '3px', fontSize: '11px', color: 'var(--info)' }}>
                Brak pozycji w dokumencie
              </div>
            )}
          </div>
        )}
      </DetailsModal>
    </div>
  );
};

export default ExternalReceipt;
